import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getCheckoutStatus } from "@/lib/api";
import { CALENDLY_URL } from "@/lib/content";
import { openCalendly } from "@/components/site/Calendly";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, CalendarHeart } from "lucide-react";

const MAX_ATTEMPTS = 6;
const SESSION_PACKAGES = ["strategy_session", "ongoing_partnership", "reading_intervention"];

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | success | pending | failed
  const [info, setInfo] = useState(null);

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState("failed");
        return;
      }
      if (attempt >= MAX_ATTEMPTS) {
        setState("pending");
        return;
      }
      try {
        const data = await getCheckoutStatus(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (e) {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background">
      <div
        data-testid="payment-return"
        className="max-w-lg w-full rounded-3xl border border-border bg-background p-10 md:p-14 text-center"
      >
        {state === "polling" && (
          <>
            <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto" strokeWidth={1.2} />
            <h1 className="mt-8 font-serif text-4xl font-light">Confirming your payment…</h1>
            <p className="mt-4 text-muted-foreground">
              One moment while we verify your transaction securely.
            </p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-primary mx-auto" strokeWidth={1.2} />
            <h1 className="mt-8 font-serif text-4xl md:text-5xl font-light leading-tight">
              Payment received. 💗
            </h1>
            <p className="mt-4 text-muted-foreground">
              Thank you{info?.package_label ? ` — ${info.package_label} is confirmed` : ""}.
              {SESSION_PACKAGES.includes(info?.package_id)
                ? " Choose a time below and we'll meet soon."
                : " Joanne will be in touch personally with your next steps."}
            </p>
            {SESSION_PACKAGES.includes(info?.package_id) && (
              <button
                data-testid="schedule-session"
                onClick={() => openCalendly(CALENDLY_URL)}
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium text-primary-foreground transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(193,119,103,0.6)]"
              >
                <CalendarHeart className="h-4 w-4" strokeWidth={1.5} />
                Schedule Your Session
              </button>
            )}
          </>
        )}

        {state === "pending" && (
          <>
            <Loader2 className="h-12 w-12 text-primary mx-auto" strokeWidth={1.2} />
            <h1 className="mt-8 font-serif text-4xl font-light">Still processing</h1>
            <p className="mt-4 text-muted-foreground">
              Your payment is taking a little longer than usual. You'll receive an
              email confirmation shortly.
            </p>
          </>
        )}

        {state === "failed" && (
          <>
            <XCircle className="h-14 w-14 text-destructive mx-auto" strokeWidth={1.2} />
            <h1 className="mt-8 font-serif text-4xl font-light">Payment not completed</h1>
            <p className="mt-4 text-muted-foreground">
              It looks like the payment was cancelled or expired. You're welcome to
              try again whenever you're ready.
            </p>
          </>
        )}

        <Link
          to="/"
          data-testid="payment-return-home"
          className="group mt-10 inline-flex items-center gap-2 rounded-full border border-foreground/25 px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium transition-all duration-300 hover:border-primary hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" strokeWidth={1.5} />
          Back to Home
        </Link>
      </div>
    </div>
  );
}
